import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FirestoreProductBisacSubject } from "reedy-data/models";
import { BasePanel } from "components/BasePanel";
import { Button } from "components/button";
import { EmptyList } from "components/empty/EmptyList";
import { BaseItem, SortableList } from "components/SortableList";
import { PanelFooter } from "components/panels";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { copyToClipboard } from "utils/helpers";
import { toast } from "utils/toast";
import {
  AddBisacCodesModal,
  type AddBisacCodesModalProps,
} from "./AddBisacCodesModal";

interface FinalBisacCodesProps {
  subjects: FirestoreProductBisacSubject[];
  onOpenModal: () => void;
  onCopy: (codesCount: number) => void;
  onAdd: AddBisacCodesModalProps["onAdd"];
  onRemove: (subject: FirestoreProductBisacSubject) => void;
  onReorder: (orderedIds: string[]) => void;
}

export const FinalBisacCodes = ({
  subjects,
  onOpenModal,
  onCopy,
  onAdd,
  onRemove,
  onReorder,
}: FinalBisacCodesProps) => {
  const { t } = useTranslation(["productDetails", "bisacCodes"]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const items: BaseItem[] = subjects.map((subject) => ({
    id: subject.id,
    value: subject.subjectCode ?? "",
    description: subject.subjectHeadingText ?? "",
  }));
  const hasCodes = items.some(({ value }) => value);

  const openModal = () => {
    onOpenModal();
    setIsModalOpen(true);
  };

  const copyCodes = () => {
    const codes = items.map(({ value }) => value).filter(Boolean);
    return copyToClipboard({
      arrayToCopy: codes,
      delimiter: "\n",
      onDone: () => {
        onCopy(codes.length);
        toast.success(t("productDetails:toasts.copyBisacCodes"));
      },
      onError: () => toast.error(t("bisacCodes:copy.failed")),
    });
  };

  const removeItem = (item: BaseItem | undefined) => {
    const subject = subjects.find(({ id }) => id === item?.id);
    if (subject) onRemove(subject);
  };

  return (
    <>
      <BasePanel
        title={t("productDetails:bisac.final.title")}
        className="flex-1 max-w-160 max-h-full"
        headerClassName="bg-primary-50"
        headerAction={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                icon="plus"
                testId="add-bisac-code"
                aria-label={t("productDetails:bisac.final.add")}
                onClick={openModal}
                className="px-0"
              />
            </TooltipTrigger>
            <TooltipContent>
              {t("productDetails:bisac.final.add")}
            </TooltipContent>
          </Tooltip>
        }
        footer={
          hasCodes && (
            <PanelFooter justify="start">
              <Button
                variant="tertiary"
                icon="copy"
                label={t("productDetails:bisac.final.copy")}
                testId="copy-bisac-codes"
                onClick={copyCodes}
              />
            </PanelFooter>
          )
        }
        isEmpty={!items.length}
        emptyComponent={
          <EmptyList
            title={t("productDetails:bisac.final.empty.title")}
            text={t("productDetails:bisac.final.empty.description")}
          />
        }
      >
        <SortableList
          items={items}
          onChangeOrder={(ordered) => onReorder(ordered.map(({ id }) => id))}
          onRemove={removeItem}
          copyToClipboard
          successToastMessage={t("productDetails:toasts.copyBisacCode")}
          valueClassName="w-22 shrink-0"
          descriptionVariant="muted"
        />
      </BasePanel>

      <AddBisacCodesModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAdd={onAdd}
      />
    </>
  );
};
